import React from 'react';
import { Tag, seg } from '../tags/Tag';
import type { EnumCellProps } from './types';

/**
 * EnumCell - Renders enum values as tags
 * 
 * Features:
 * - Option-specific labels, icons, colors
 * - Click handlers
 * - Configurable variant and styling
 */
export const EnumCell: React.FC<EnumCellProps> = ({ value, config, row }) => {
  if (value === null || value === undefined) {
    return null;
  }
  
  // Get the actual enum value to use for lookup
  const enumValue = config?.getValue 
    ? config.getValue(value, row)
    : String(value);
  
  // Check if there's a specific option configuration for this value
  const optionConfig = config?.options?.[enumValue];
  
  // Determine which click handler to use (option-specific or default)
  const handleClick = optionConfig?.onClick 
    ? () => optionConfig.onClick!(value, row)
    : config?.onClick 
      ? () => config.onClick!(value, row)
      : undefined;
  
  // Determine display value (option-specific label, default displayValue, or raw value)
  const displayValue = optionConfig?.label 
    ? optionConfig.label(value, row)
    : config?.displayValue 
      ? config.displayValue(value, row)
      : String(value);
  
  // Use option-specific icon if available, otherwise default icon
  const IconComponent = optionConfig?.icon || config?.icon;
  
  return (
    <Tag
      segments={[
        ...(IconComponent ? [seg.icon(IconComponent)] : []),
        seg.text(displayValue)
      ]}
      shape="rect"
      glassLevel="strong"
      border="none"
      onClick={handleClick}
      size="sm"
      visualOnly={!handleClick && !config?.buttonOnly}
      tabularNums={config?.tabularNums}
    />
  );
};

